import clsx from "clsx"
import { App, ModalFuncProps } from "antd"
import { HookAPI } from "antd/es/modal/useModal"
import { CloseCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "@hooks"
import { AUTO_CLOSE_MODAL_MILLISECONDS, MODAL_TYPES } from "@constants"

type ModalType = keyof HookAPI | "notification"

export function useModal() {
  const t = useTranslation("common.modal")
  const modal = App.useApp().modal
  const defaultOptions: Partial<ModalFuncProps> = {
    closable: false,
    centered: true,
    icon: null,
    getContainer: false,
  }

  function getModalWithType(type: ModalType, options: Partial<ModalFuncProps>) {
    const fullOptions = {
      ...defaultOptions,
      wrapClassName: type,
      ...options,
    }

    if (type === MODAL_TYPES.NOTIFICATION) {
      return modal[MODAL_TYPES.SUCCESS](fullOptions)
    }

    return modal[type](fullOptions)
  }

  const success = (
    options?: Partial<ModalFuncProps> & { message?: string },
    duration = AUTO_CLOSE_MODAL_MILLISECONDS
  ) => {
    const notificationModal = getModalWithType(MODAL_TYPES.SUCCESS, {
      footer: null,
      content: <span className="flex-center margin-y-10 font-bold">{options?.message || t("successDescription")}</span>,
      ...options,
      wrapClassName: clsx("notification", options?.wrapClassName),
      className: clsx(options?.className, { content: !!options?.message }),
    })

    setTimeout(notificationModal.destroy, duration)

    return notificationModal
  }

  const successCancel = (options?: Partial<ModalFuncProps>, duration = AUTO_CLOSE_MODAL_MILLISECONDS) => {
    return success({ message: t("successCancelDescription") }, duration)
  }

  const confirm = (options?: Partial<ModalFuncProps>) => {
    return getModalWithType(MODAL_TYPES.CONFIRM, {
      title: null,
      content: t("confirmDiscardEditedContentDescription"),
      okText: t("btn.ok"),
      cancelText: t("btn.cancel"),
      maskClosable: true,
      cancelButtonProps: {
        color: "primary",
        variant: "outlined",
        className: "background-white padding-x-16 padding-y-5 margin-right-20",
      },
      okButtonProps: {
        color: "primary",
        variant: "solid",
        className: "padding-x-37 padding-y-5 margin-left-0",
      },
      ...options,
    })
  }

  const confirmSubmit = (options?: Partial<ModalFuncProps>) => {
    return confirm({ title: t("confirmSubmit"), content: "", okText: t("btn.okSubmit"), ...options })
  }

  const confirmCancel = (options?: Partial<ModalFuncProps>) => {
    return confirm({ title: t("confirmCancel"), content: "", okText: t("btn.okCancel"), ...options })
  }

  const confirmLeavePage = (options?: Partial<ModalFuncProps>) => {
    return confirm({
      title: (
        <span>
          {t("confirmLeavePage")}
          <br />
          {t("confirmLeavePageAlert")}
        </span>
      ),
      content: "",
      okText: t("btn.okCancel"),
      ...options,
    })
  }

  const error = (options?: Partial<ModalFuncProps>) => {
    return getModalWithType(MODAL_TYPES.ERROR, {
      icon: <CloseCircleOutlined className="color-red" />,
      title: <span className="font-bold font-size-24 line-height-24">{t("errorTitle")}</span>,
      cancelText: t("btn.cancel"),
      okButtonProps: {
        color: "primary",
        variant: "solid",
        className: "padding-x-16 padding-y-5 margin-left-0",
      },
      ...options,
      content: <span className="white-space-break">{options?.content || t("errorContent")}</span>,
    })
  }

  const notification = (options: Partial<ModalFuncProps> = {}, duration = AUTO_CLOSE_MODAL_MILLISECONDS) => {
    const notificationModal = getModalWithType(MODAL_TYPES.NOTIFICATION, {
      footer: null,
      ...options,
      wrapClassName: clsx("notification", options.wrapClassName),
      className: clsx(options.className, { content: !!options.content }),
    })

    setTimeout(notificationModal.destroy, duration)

    return notificationModal
  }

  return {
    notification,
    success,
    successCancel,
    confirm,
    confirmSubmit,
    confirmCancel,
    confirmLeavePage,
    error,
    info: (options: Partial<ModalFuncProps>) => getModalWithType(MODAL_TYPES.INFO, options),
    warning: (options: Partial<ModalFuncProps>) => getModalWithType(MODAL_TYPES.WARNING, options),
  }
}
